import { ECardTypeTemplateId } from 'types/cardTypeMessage';
import { cn } from 'utils/cn';

const bar = 'rounded-sm bg-neutral-200';
const barDark = 'rounded-sm bg-neutral-300';

function SkeletonBar({
  className,
  dark,
}: {
  className?: string;
  dark?: boolean;
}) {
  return <div className={cn(dark ? barDark : bar, className)} />;
}

function ProductsSkeleton() {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-md border border-neutral-200 bg-white">
      <div className="relative flex min-h-0 flex-1">
        <SkeletonBar className="absolute left-1 top-1 z-10 h-2 w-6" />
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-px bg-neutral-200">
          <SkeletonBar className="min-h-[52px] rounded-none" dark />
          <div className="grid grid-rows-2 gap-px">
            <SkeletonBar className="rounded-none" dark />
            <SkeletonBar className="rounded-none" dark />
          </div>
        </div>
      </div>
      <div className="space-y-1 border-t border-neutral-100 p-1.5">
        <SkeletonBar className="h-2 w-[70%]" />
        <SkeletonBar className="h-1.5 w-full" />
        <SkeletonBar className="h-1.5 w-[85%]" />
        <SkeletonBar className="ml-auto h-2 w-[35%]" />
        <SkeletonBar className="h-2 w-full" />
        <SkeletonBar className="h-2 w-full" />
      </div>
    </div>
  );
}

function PlacesSkeleton() {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-md border border-neutral-200 bg-white">
      <div className="relative flex min-h-0 flex-1">
        <SkeletonBar className="absolute left-1 top-1 z-10 h-2 w-6" />
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-px bg-neutral-200">
          <SkeletonBar className="min-h-[52px] rounded-none" dark />
          <div className="grid grid-rows-2 gap-px">
            <SkeletonBar className="rounded-none" dark />
            <SkeletonBar className="rounded-none" dark />
          </div>
        </div>
      </div>
      <div className="space-y-1 border-t border-neutral-100 p-1.5">
        <SkeletonBar className="h-2 w-[65%]" />
        <div className="flex items-center gap-0.5">
          <span className="text-[8px] text-neutral-400">📍</span>
          <SkeletonBar className="h-1.5 flex-1" />
        </div>
        <div className="flex items-center gap-0.5">
          <span className="text-[8px] text-neutral-400">🕐</span>
          <SkeletonBar className="h-1.5 flex-1" />
        </div>
        <SkeletonBar className="h-2 w-full" />
        <SkeletonBar className="h-2 w-full" />
      </div>
    </div>
  );
}

function PeopleSkeleton() {
  return (
    <div className="flex h-full w-full flex-col items-center overflow-hidden rounded-md border border-neutral-200 bg-white px-2 pb-2 pt-3">
      <div className="mb-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-neutral-100">
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 text-neutral-300"
          aria-hidden
        >
          <circle cx="12" cy="8" r="3.5" fill="currentColor" />
          <path fill="currentColor" d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" />
        </svg>
      </div>
      <div className="mb-1 flex flex-wrap justify-center gap-0.5">
        <SkeletonBar className="h-1.5 w-7 rounded-full" />
        <SkeletonBar className="h-1.5 w-7 rounded-full" />
        <SkeletonBar className="h-1.5 w-7 rounded-full" />
      </div>
      <SkeletonBar className="mb-1 h-2 w-[55%]" />
      <SkeletonBar className="h-1.5 w-[75%]" />
      <SkeletonBar className="mt-auto h-2 w-full" />
    </div>
  );
}

function PicturesSkeleton() {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-md border border-neutral-200 bg-white">
      <SkeletonBar className="absolute left-1 top-1 z-10 h-2 w-6" />
      <SkeletonBar className="h-full w-full rounded-none" dark />
      <SkeletonBar className="absolute bottom-3 left-1/2 z-10 h-2 w-[55%] -translate-x-1/2" />
    </div>
  );
}

/** Shared frame in template picker — portrait types fill it; Pictures sits square at bottom */
const TEMPLATE_THUMB_FRAME_CLASS =
  'flex h-[188px] w-[150px] max-w-full shrink-0 items-end justify-center overflow-hidden rounded-lg bg-neutral-50 p-1.5 transition';
const TEMPLATE_THUMB_PICTURES_INNER_CLASS = 'h-[150px] w-[150px] shrink-0';

const SKELETON_BY_ID: Record<ECardTypeTemplateId, () => JSX.Element> = {
  [ECardTypeTemplateId.Products]: ProductsSkeleton,
  [ECardTypeTemplateId.Places]: PlacesSkeleton,
  [ECardTypeTemplateId.People]: PeopleSkeleton,
  [ECardTypeTemplateId.Pictures]: PicturesSkeleton,
};

type Props = {
  templateId: ECardTypeTemplateId;
  label: string;
  isSelected: boolean;
  onClick: () => void;
};

export function CardTypeMessageTemplateThumbnail({
  templateId,
  label,
  isSelected,
  onClick,
}: Props) {
  const Skeleton = SKELETON_BY_ID[templateId];

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full min-w-0 flex-col items-center gap-2 text-left"
    >
      <div
        className={cn(
          TEMPLATE_THUMB_FRAME_CLASS,
          isSelected
            ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-white'
            : 'ring-1 ring-neutral-200',
        )}
      >
        {templateId === ECardTypeTemplateId.Pictures ? (
          <div className={TEMPLATE_THUMB_PICTURES_INNER_CLASS}>
            <Skeleton />
          </div>
        ) : (
          <div className="h-full w-full min-h-0">
            <Skeleton />
          </div>
        )}
      </div>
      <span className="flex items-center gap-2">
        <span
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
            isSelected
              ? 'border-emerald-500 bg-emerald-500'
              : 'border-neutral-400 bg-white',
          )}
          aria-hidden
        >
          {isSelected ? (
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
          ) : null}
        </span>
        <span className="text-sm font-medium text-neutral-800">{label}</span>
      </span>
    </button>
  );
}
